import React, { useState, useCallback } from 'react';
import { Button, Modal, message, Typography, Space, Progress } from 'antd';
import { 
  AudioOutlined, 
  StopOutlined, 
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  SendOutlined
} from '@ant-design/icons';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useVoiceRecording, RecordingState } from '../hooks/useVoiceRecording';
import VoiceWaveform from './VoiceWaveform';

const { Text, Title } = Typography;

// 主容器
const VoiceInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
`;

// 录音按钮样式
const RecordButton = styled.div<{ $isRecording: boolean; $disabled: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  font-size: 28px;
  margin-bottom: 20px;
  position: relative;
  
  background: ${props => {
    if (props.$disabled) return 'var(--bg-disabled, #f5f5f5)';
    if (props.$isRecording) return 'linear-gradient(135deg, #ff4757, #ff6b7a)';
    return 'linear-gradient(135deg, #10a37f, #1a73e8)';
  }};
  
  color: ${props => props.$disabled ? 'var(--text-muted, #9ca3af)' : '#ffffff'};
  
  box-shadow: ${props => {
    if (props.$disabled) return 'none';
    if (props.$isRecording) return '0 0 30px rgba(255, 71, 87, 0.5)';
    return '0 0 30px rgba(16, 163, 127, 0.3)';
  }};
  
  &:hover {
    transform: ${props => props.$disabled ? 'none' : 'scale(1.05)'};
  }
  
  &:active {
    transform: ${props => props.$disabled ? 'none' : 'scale(0.95)'};
  }
`;

// 状态文本
const StatusText = styled.div<{ $state: RecordingState }>`
  text-align: center;
  margin-bottom: 20px;
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    color: ${props => {
      switch (props.$state) {
        case 'recording': return 'var(--primary-color, #10a37f)';
        case 'processing': return 'var(--warning-color, #faad14)';
        case 'error': return 'var(--error-color, #ff4d4f)';
        case 'completed': return 'var(--success-color, #52c41a)';
        default: return 'var(--text-primary, #2d3748)';
      }
    }};
  }
  
  .subtitle {
    font-size: 14px;
    color: var(--text-secondary, #8e8ea0);
    line-height: 1.5;
  }
`;

// 控制按钮区域
const ControlButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 20px;
`;

// 控制按钮
const ControlButton = styled(Button)<{ $variant?: 'danger' | 'primary' | 'default' }>`
  &.ant-btn {
    height: 40px;
    border-radius: 20px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 500;
    
    ${props => props.$variant === 'danger' && css`
      background: linear-gradient(135deg, #ff4757, #ff6b7a);
      border: none;
      color: white;
      
      &:hover {
        background: linear-gradient(135deg, #ff3742, #ff5865);
        color: white;
      }
    `}
    
    ${props => props.$variant === 'primary' && css`
      background: linear-gradient(135deg, #10a37f, #1a73e8);
      border: none;
      color: white;
      
      &:hover {
        background: linear-gradient(135deg, #0e8f6f, #1557d1);
        color: white;
      }
    `}
  }
`;

// 转录结果显示
const TranscriptDisplay = styled.div`
  width: 100%;
  margin-top: 20px;
  padding: 16px;
  border-radius: 12px;
  background: var(--bg-secondary, #f7f7f8);
  border: 1px solid var(--border-color, #e5e7eb);
  
  .transcript-label {
    font-size: 12px;
    font-weight: 500;
    color: var(--text-secondary, #8e8ea0);
    margin-bottom: 8px;
    text-transform: uppercase;
  }
  
  .transcript-text {
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-primary, #2d3748);
    white-space: pre-wrap;
  }
`;

// 错误显示
const ErrorDisplay = styled.div`
  width: 100%;
  margin-top: 20px;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 77, 79, 0.1);
  border: 1px solid rgba(255, 77, 79, 0.3);
  color: var(--error-color, #ff4d4f);
  font-size: 14px;
  text-align: center;
`;

// 进度指示器
const ProgressIndicator = styled.div`
  width: 100%;
  margin-top: 16px;
  text-align: center;
  
  .ant-progress-text {
    color: var(--text-secondary, #8e8ea0);
    font-size: 12px;
  }
`;

interface VoiceInputProps {
  onTranscriptReceived?: (transcript: string) => void;
  onAudioReceived?: (audioBlob: Blob) => void;
  disabled?: boolean;
  maxDuration?: number;
  language?: string; // 指定语音识别语言，如 'zh', 'ja', 'en'，不指定则自动检测界面语言
  showLanguageSelector?: boolean; // 是否显示语言选择器，默认false
  className?: string;
}

/**
 * 语音输入组件
 * 提供ChatGPT风格的语音录制和转录功能
 */
const VoiceInput: React.FC<VoiceInputProps> = ({
  onTranscriptReceived,
  onAudioReceived,
  disabled = false,
  maxDuration = 60,
  language, // 指定的语音识别语言
  showLanguageSelector = false, // 是否显示语言选择器
  className
}) => {
  const { t, i18n } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    language || localStorage.getItem('preferred_language') || 'ja'
  );

  // 当界面语言变化时，同步更新语音识别语言（如果没有明确指定语言）
  React.useEffect(() => {
    if (!language) { // 只有在没有明确指定语言时才自动同步
      const currentLang = localStorage.getItem('preferred_language') || i18n.language || 'ja';
      setSelectedLanguage(currentLang);
    }
  }, [i18n.language, language]);
  
  // 创建国际化错误处理函数
  const handleError = (errorType: string, params?: any) => {
    let errorMessage: string = '';
    switch (errorType) {
      case 'NotAllowedError':
        errorMessage = t('chat.voiceInputErrorNotAllowed') as string;
        break;
      case 'NotFoundError':
        errorMessage = t('chat.voiceInputErrorNotFound') as string;
        break;
      case 'NotReadableError':
        errorMessage = t('chat.voiceInputErrorNotReadable') as string;
        break;
      case 'OverconstrainedError':
        errorMessage = t('chat.voiceInputErrorOverconstrained') as string;
        break;
      case 'minDuration':
        errorMessage = t('chat.voiceInputErrorMinDurationFormat', params) as string;
        break;
      case 'transcribe':
        errorMessage = t('chat.voiceInputErrorTranscribe') as string;
        break;
      case 'network':
        errorMessage = t('chat.voiceInputErrorNetwork') as string;
        break;
      case 'format':
        errorMessage = t('chat.voiceInputErrorFormat') as string;
        break;
      case 'server':
        errorMessage = t('chat.voiceInputErrorServer') as string;
        break;
      default:
        errorMessage = t('chat.voiceInputErrorGeneral') as string;
    }
    message.error(errorMessage);
  };

  // 语音录制Hook
  const {
    isRecording,
    recordingState,
    duration,
    audioLevel,
    startRecording,
    stopRecording,
    cancelRecording,
    audioBlob,
    transcript,
    error
  } = useVoiceRecording({
    maxDuration,
    language: selectedLanguage, // 传递选中的语言
    onTranscriptReceived: (transcript) => {
      // 录音完成后稍等一下再自动关闭弹窗，让用户看到完成状态
      if (transcript.trim()) {
        setTimeout(() => {
          setShowModal(false);
          onTranscriptReceived?.(transcript);
          message.success(t('chat.voiceInputAutoComplete'));
        }, 800); // 0.8秒后自动关闭
      }
    },
    onAudioReceived: (audioBlob) => {
      // 传递音频文件给父组件
      onAudioReceived?.(audioBlob);
    },
    onError: handleError
  });

  // 获取状态文本
  const getStatusText = () => {
    switch (recordingState) {
      case 'idle':
        return {
          title: t('chat.voiceInputStatusReady'),
          subtitle: t('chat.voiceInputStatusReadySubtitle')
        };
      case 'recording':
        return {
          title: t('chat.voiceInputStatusRecording'),
          subtitle: t('chat.voiceInputStatusRecordingSubtitle')
        };
      case 'processing':
        return {
          title: t('chat.voiceInputStatusProcessing'),
          subtitle: t('chat.voiceInputStatusProcessingSubtitle')
        };
      case 'completed':
        return {
          title: t('chat.voiceInputStatusCompleted'),
          subtitle: t('chat.voiceInputStatusCompletedSubtitle')
        };
      case 'error':
        return {
          title: t('chat.voiceInputStatusError'),
          subtitle: t('chat.voiceInputStatusErrorSubtitle')
        };
      default:
        return { title: '', subtitle: '' };
    }
  };

  // 处理录音按钮点击
  const handleRecordClick = async () => {
    if (disabled) return;
    
    if (isRecording) {
      stopRecording();
    } else {
      try {
        await startRecording();
      } catch (error) {
        // 错误已在Hook中处理
      }
    }
  };



  // 处理取消按钮
  const handleCancel = () => {
    cancelRecording();
    setShowModal(false);
  };

  // 处理重新录音
  const handleRetry = () => {
    cancelRecording();
    setTimeout(() => {
      startRecording();
    }, 100);
  };

  const statusText = getStatusText();

  return (
    <>
      {/* 录音按钮 */}
      <Button
        type="text"
        icon={<AudioOutlined />}
        onClick={() => setShowModal(true)}
        disabled={disabled}
        style={{
          border: 'none',
          background: 'transparent',
          color: 'var(--text-secondary, #8e8ea0)',
          width: '32px',
          height: '32px',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          padding: 0
        }}
        title={t('chat.voiceInput')}
      />

      {/* 录音模态框 */}
      <Modal
        title={`🎤 ${t('chat.voiceInputTitle')}`}
        open={showModal}
        onCancel={handleCancel}
        footer={null}
        width={500}
        destroyOnHidden
        className={className}
      >
        <VoiceInputContainer>
          {/* 语言选择器 */}
          {showLanguageSelector && (
            <div style={{ marginBottom: '20px', textAlign: 'center' }}>
              <label style={{ marginRight: '10px', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                {i18n.language === 'zh' ? '识别语言:' : '認識言語:'}
              </label>
              <select 
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                style={{
                  padding: '6px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white',
                  cursor: isRecording ? 'not-allowed' : 'pointer'
                }}
                disabled={isRecording}
              >
                <option value="zh">中文 (Chinese)</option>
                <option value="ja">日本語 (Japanese)</option>
                <option value="en">English</option>
              </select>
            </div>
          )}

          {/* 波形动画 */}
          <VoiceWaveform
            isRecording={isRecording}
            audioLevel={audioLevel}
            duration={duration}
            size={120}
            showDuration={true}
          />

          {/* 录音按钮 */}
          <RecordButton
            $isRecording={isRecording}
            $disabled={disabled || recordingState === 'processing'}
            onClick={handleRecordClick}
          >
            {isRecording ? <StopOutlined /> : <AudioOutlined />}
          </RecordButton>

          {/* 状态文本 */}
          <StatusText $state={recordingState}>
            <div className="title">{statusText.title}</div>
            <div className="subtitle">{statusText.subtitle}</div>
          </StatusText>

          {/* 进度指示器 */}
          {recordingState === 'processing' && (
            <ProgressIndicator>
              <Progress 
                percent={66} 
                showInfo={false} 
                strokeColor={{
                  '0%': '#10a37f',
                  '100%': '#1a73e8',
                }}
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                {t('chat.voiceInputProcessingLabel')}
              </div>
            </ProgressIndicator>
          )}

          {/* 录音时长进度 */}
          {isRecording && (
            <ProgressIndicator>
              <Progress 
                percent={(duration / maxDuration) * 100}
                showInfo={false}
                strokeColor="#10a37f"
                trailColor="rgba(16, 163, 127, 0.1)"
              />
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                {Math.floor(duration)}s / {maxDuration}s
              </div>
            </ProgressIndicator>
          )}

          {/* 转录结果 */}
          {transcript && (
            <TranscriptDisplay>
              <div className="transcript-label">{t('chat.voiceInputTranscriptLabel')}</div>
              <div className="transcript-text">{transcript}</div>
            </TranscriptDisplay>
          )}

          {/* 错误显示 */}
          {error && (
            <ErrorDisplay>
              {error}
            </ErrorDisplay>
          )}

          {/* 控制按钮 */}
          {(recordingState === 'recording' || recordingState === 'error') && (
            <ControlButtons>
              {/* 取消按钮 */}
              <ControlButton onClick={handleCancel}>
                <CloseOutlined />
                {t('chat.voiceInputCancel')}
              </ControlButton>

              {/* 重新录音按钮 - 只在错误时显示 */}
              {recordingState === 'error' && (
                <ControlButton $variant="primary" onClick={handleRetry}>
                  <AudioOutlined />
                  {t('chat.voiceInputRetry')}
                </ControlButton>
              )}
            </ControlButtons>
          )}
        </VoiceInputContainer>
      </Modal>
    </>
  );
};

export default VoiceInput; 